/**
 * Share Code Codec
 * Encode/decode HookHouse configs as compact shareable strings.
 * Format: "HH1:<lz-string compressed payload>"
 */

import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { ShareCodePayload } from '@/types/shareCode.types';
import { TemplateConfig } from '@/types/template.types';

const SHARE_CODE_PREFIX = 'HH1:';
const CURRENT_VERSION = 1;

/**
 * Encode a TemplateConfig into a shareable string.
 * Pipeline: TemplateConfig → ShareCodePayload → JSON.stringify → lz-string compress → prefix
 */
export function encodeShareCode(config: TemplateConfig, name?: string): string {
  const payload: ShareCodePayload = {
    v: CURRENT_VERSION,
    t: Math.floor(Date.now() / 1000),
    c: config,
    ...(name ? { n: name } : {})
  };

  const json = JSON.stringify(payload);
  const compressed = compressToEncodedURIComponent(json);

  return `${SHARE_CODE_PREFIX}${compressed}`;
}

/**
 * Decode a share code string back into a TemplateConfig.
 * Returns { config, name, version, timestamp } on success, or { error } on failure.
 */
export function decodeShareCode(code: string): {
  config: TemplateConfig;
  name?: string;
  version: number;
  timestamp: number;
} {
  // Validate prefix
  if (!code.startsWith(SHARE_CODE_PREFIX)) {
    throw new Error('Invalid share code: missing HH1: prefix');
  }

  const compressed = code.slice(SHARE_CODE_PREFIX.length);
  if (!compressed) {
    throw new Error('Invalid share code: empty payload');
  }

  // Decompress
  const json = decompressFromEncodedURIComponent(compressed);
  if (!json) {
    throw new Error('Invalid share code: decompression failed');
  }

  // Parse JSON
  let payload: ShareCodePayload;
  try {
    payload = JSON.parse(json);
  } catch {
    throw new Error('Invalid share code: corrupted data');
  }

  // Validate structure
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid share code: not an object');
  }
  if (typeof payload.v !== 'number') {
    throw new Error('Invalid share code: missing version');
  }
  if (!payload.c || typeof payload.c !== 'object') {
    throw new Error('Invalid share code: missing config');
  }

  // Validate config has expected shape
  const config = payload.c;
  if (!config.vocalConfig || !Array.isArray(config.musicalBlend) || !Array.isArray(config.moodArchitecture)) {
    throw new Error('Invalid share code: incomplete config');
  }

  return {
    config,
    name: payload.n,
    version: payload.v,
    timestamp: payload.t || 0
  };
}

/**
 * Validate a share code without fully decoding
 */
export function isValidShareCode(code: string): boolean {
  try {
    decodeShareCode(code);
    return true;
  } catch {
    return false;
  }
}
